'use client';

import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterItem } from '@/components/organisms/filter-card-list/filter-card-list';
import {
  AreaData,
  DateData,
  LocationChangeParameters,
  LocationData,
  PersonsData,
  Room,
  RoomsState,
  SearchBarDefaultValues,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import {
  extractAdultCount,
  extractChildrenAges,
  extractDepartureDate,
  extractDurationRange,
  extractDurationSecondary,
  extractTransportationMethod,
  extractTravelPortalLocationCodes,
  generateDateSectionValueString,
  generatePersonsSectionValueString,
  getArea,
  getAreaByValue,
  getAvailablePeriodTypesFromDates,
  getTravelPortalAreaDefaults,
  isPersonsSectionValid,
  mergeObjects,
} from '@/components/organisms/search-bar/shared/search-utils/search-utils';
import { trackSubmitSearchEvent } from '@/integrations/tracking/google-tag-manager/scripts';
import { AdaptDestinationHierarchy } from '@/integrations/travelportal/content/content-destinations/destinations-hierarchy/destination-hierarchy.adapter';
import useQueryDestinationHierarchy from '@/integrations/travelportal/content/content-destinations/destinations-hierarchy/destination-hierarchy.hooks';
import { AdaptSearchAvailableDates } from '@/integrations/travelportal/offers/search/available-dates/available-dates.adapter';
import useQuerySearchAvailableDates from '@/integrations/travelportal/offers/search/available-dates/available-dates.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useRecentSearchesContext } from '@/libraries/contexts/recent-searches-context/recent-searches-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  searchPageEvents$,
  SearchPageEventType,
} from '@/libraries/events/page-events';
import parseDanishDate from '@/libraries/formatters/parse-danish-date';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { getRangeEndDate } from '@/libraries/getters/get-range-end-date';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import useUiEventOutsideHandler from '@/libraries/hooks/use-ui-event-outside';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import validateSameDate from '@/libraries/validators/validate-same-date';
import SearchBarWrapper from './search-bar-wrapper/search-bar-wrapper';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './search-bar.module.scss';

export interface SearchBarProps {
  /** Indicates whether to automatically trigger search when no URL parameters are present */
  autoSearch?: boolean;
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether to show the compact version */
  compact?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** Default values for the search bar */
  propDefaultValues?: SearchBarDefaultValues;
  /** The search settings */
  searchSettings: Content.Components['SearchSettingsDto'];
  /** Indicates weather the search should trigger a navigation to the search page (used when the search bar is used on a different page)*/
  shouldNavigate?: boolean;
  /** Indicates weather the search should trigger a refresh of the page with the updated url params */
  shouldRefresh?: boolean;
  /** The travel portal search settings */
  travelPortalSearchSettings?:
    | Content.Components['TravelPortalSearchSettingsDto']
    | undefined;
}

export const SearchBar = forwardRef(
  (
    {
      autoSearch = false,
      buttonMode = false,
      className,
      compact = false,
      shouldNavigate = false,
      shouldRefresh = false,
      travelPortalSearchSettings,
    }: SearchBarProps,
    ref,
  ) => {
    const { t } = useI18n();
    const router = useRouter();
    const { dateFormatter } = useDateFormatter();
    const rootRef = useRef<HTMLDivElement>(null);
    const searchParams = useSearchParams();
    const urlDefaultValues = getSearchParameters(searchParams!);
    const {
      activeSection,
      adultsNo,
      area,
      childrenAges,
      departureDate,
      departureIds,
      setActiveSection,
      setAdultsNo,
      setArea,
      setChildrenAges,
      setCountryIds,
      setDepartureDate,
      setDepartureIds,
      setDestinationIds,
      setPeriodType,
      setTriggeredBy,
      updateCookies,
    } = useSearchContext();
    const [recentSearchLocationText, setRecentSearchLocationText] = useState<
      string[] | string
    >();
    const { addRecentSearch } = useRecentSearchesContext();
    const { configurationFlowAnchorLinkId } = usePageDetailsContext();
    const isLaptop = useWindowSize('laptop');

    const hasDefaultValue = (parameterName: string): boolean => {
      // Check if parameter is in searchParams
      const isInQuery = searchParams!.has(parameterName);

      // Check if parameter is in searchDefaults
      const isInDefaults = travelPortalSearchSettings
        ? Object.hasOwnProperty.call(travelPortalSearchSettings, parameterName)
        : false;

      return isInQuery || isInDefaults;
    };

    const [areaSection, setAreaSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          areas: travelPortalSearchSettings?.availableAreas,
        } as AreaData,
        id: 0,
        label: '',
        type: SearchBarWrapperSectionType.AREA,
      },
    );

    const [locationSection, setLocationSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          locations: [],
          transportationOptions: [],
        } as LocationData,
        id: 1,
        isLoading:
          hasDefaultValue('destinationIds') || hasDefaultValue('departureIds'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.location.locationTitle'),
        placeholder: t('items.search.searchBar.location.locationPlaceholder'),
        type: SearchBarWrapperSectionType.LOCATIONS,
      });

    const adultsNumber =
      getTravelPortalAreaDefaults(travelPortalSearchSettings, area)
        .defaultPaxDistribution?.adultsNo ?? 2;

    const [personsSection, setPersonsSection] =
      useState<SearchBarWrapperSectionData>({
        dropdownData: {
          adultThreshold: travelPortalSearchSettings?.adultAgeThreshold,
          defaultAdultNumber: adultsNumber,
          maxPersons: travelPortalSearchSettings?.maxNumberOfAdults,
          roomsState: {
            automaticRoomAssignment:
              !!travelPortalSearchSettings?.automaticRoomAssignment,
            rooms: [
              {
                childrenAges: [],
                numberOfAdults: adultsNumber ?? 0,
                numberOfChildren: 0,
              },
            ],
          },
        },
        id: 2,
        isLoading:
          hasDefaultValue('adultsNumber') || hasDefaultValue('childrenAges'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.persons.personsTitle'),
        placeholder: t('items.search.searchBar.persons.personsPlaceholder'),
        type: SearchBarWrapperSectionType.PERSONS,
        value: generatePersonsSectionValueString({
          adults: adultsNumber,
          children: 0,
          t,
        }),
      });

    const [dateSection, setDateSection] = useState<SearchBarWrapperSectionData>(
      {
        dropdownData: {
          availableDates: [],
          durationOptions: [],
        },
        id: 3,
        isLoading: hasDefaultValue('departureDate'),
        // TODO: replace with data from search settings when available
        label: t('items.search.searchBar.date.dateTitle'),
        placeholder: t('items.search.searchBar.date.datePlaceholder'),
        type: SearchBarWrapperSectionType.DATE,
      },
    );

    const generateLocationValue = useCallback(
      ({
        locations,
        transportationOptions,
      }: {
        locations?: LocationData['locations'];
        transportationOptions?: FilterItem[];
      }): string[] => {
        const activeTransports =
          transportationOptions
            ?.filter(option => option.isSelected && option.headline)
            .map(option => option.headline) || [];

        const activeLocations =
          locations
            ?.flatMap(group =>
              group.options.filter(
                option => option.checked && !option.disabled && option.label,
              ),
            )
            .map(option => option.label) || [];

        const hasTransports = activeTransports.length > 0;
        const hasLocations = activeLocations.length > 0;

        if (hasTransports) {
          const transportsString = activeTransports.join(', ');

          if (hasLocations) {
            setRecentSearchLocationText([...(activeLocations as string[])]);

            return [
              `${transportsString} ${t('items.common.words.to')}`,
              ...(activeLocations as string[]),
            ];
          }

          setRecentSearchLocationText(
            `${t('items.search.searchBar.location.toAllDestinations')}`,
          );

          return [
            `${transportsString} ${t('items.search.searchBar.location.toAllDestinations')}`,
          ];
        }

        return activeLocations as string[];
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const getDuration = useCallback(() => {
      return extractDurationSecondary(dateSection);
    }, [dateSection]);

    const getDurationRange = useCallback(() => {
      return extractDurationRange(dateSection);
    }, [dateSection]);

    const getTransportationMethod = useCallback(() => {
      return extractTransportationMethod(locationSection, false);
    }, [locationSection]);

    const getCheckedLocationCodes = useCallback(() => {
      return extractTravelPortalLocationCodes(locationSection);
    }, [locationSection]);

    const getAdultCount = useCallback(() => {
      return extractAdultCount(personsSection);
    }, [personsSection]);

    const getChildrenAges = useCallback(() => {
      return extractChildrenAges(personsSection);
    }, [personsSection]);

    const getDepartureDate = useCallback(() => {
      return extractDepartureDate(dateSection);
    }, [dateSection]);

    const getDepartureDateParameter = useCallback((): Date | undefined => {
      const departureDateParameter = urlDefaultValues.departureDate
        ? parseDanishDate(urlDefaultValues.departureDate)
        : undefined;

      return departureDateParameter;
    }, [urlDefaultValues]);

    const {
      data: dataDestinationHierarchy,
      isFetching: isFetchingDestinationHierarchy,
    } = useQueryDestinationHierarchy({
      area: getArea({ areaId: area, travelPortalSearchSettings }),
    });

    const {
      countryIds: checkedCountryIds,
      destinationIds: checkedDestinationIds,
    } = getCheckedLocationCodes();

    const { data: dataAvailableDates, isFetching: isFetchingAvailableDates } =
      useQuerySearchAvailableDates({
        adultsNumber: getAdultCount()?.toString() as string,
        area: getArea({ areaId: area, travelPortalSearchSettings }),
        childrenAges: getChildrenAges(),
        countryIds: checkedCountryIds || undefined,
        departureIds: getTransportationMethod() as string,
        destinationIds: checkedDestinationIds || undefined,
      });

    const getDurationOptionsFromSearchSettings = (
      selectedDurationType?: string,
    ) => {
      const activeDurationType =
        selectedDurationType ||
        urlDefaultValues.durationType ||
        (getTravelPortalAreaDefaults(travelPortalSearchSettings, area)
          .defaultPeriodType as string);

      return (
        travelPortalSearchSettings?.availablePeriodTypes?.map(option => {
          let rangeSize: number;

          // TODO: Handle rangeSize issue (#2311) gracefully
          switch (option.value) {
            case 'week':
              rangeSize = 7;
              break;

            case 'twoWeeks':
              rangeSize = 14;
              break;

            case 'threeWeeks':
              rangeSize = 21;
              break;

            default: {
              rangeSize = 0;
            }
          }

          return {
            headline: option.label,
            icon: 'Calendar' as IconName,
            id: option.value,
            isSelected: option.value === activeDurationType,
            rangeSize,
            text: option.description,
          };
        }) ?? []
      );
    };

    const setupDurationOptions = () => {
      const newDurationOptions = getDurationOptionsFromSearchSettings();

      setDateSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as DateData),
            durationOptions: newDurationOptions,
            rangeSize: undefined,
          },
        };
      });
    };

    useEffect(() => {
      const updateDestinationsAndDeparturesInLocationSection = (
        data: AdaptDestinationHierarchy,
      ) => {
        setLocationSection(previousSection => {
          // Departures
          const selectedDepartureId = getTravelPortalAreaDefaults(
            travelPortalSearchSettings,
            area,
          ).defaultDepartureCode as string;

          const updatedTransportationOptions = data.departures.map(
            departure => ({
              ...departure,
              isSelected: departure.id === selectedDepartureId,
            }),
          );

          // Destinations
          // Pre-select destinations from URL params
          const urlCountryIds = urlDefaultValues.countryIds ?? [];
          const urlDestinationIds = urlDefaultValues.destinationIds ?? [];

          // disable locations that are not available for selected transportation method
          const updatedGroupedLocations = data.destinations?.map(group => ({
            ...group,
            options: group.options.map(destination => {
              const isAvailableForDeparture = selectedDepartureId
                ? destination.departureCodes?.includes(selectedDepartureId)
                : true;

              const isSelectedFromUrl =
                urlDestinationIds.includes(destination.id) ||
                urlCountryIds.includes(group.countryCode ?? '');

              return {
                ...destination,
                checked: isAvailableForDeparture ? isSelectedFromUrl : false,
                disabled: !isAvailableForDeparture,
              };
            }),
          }));

          return {
            ...previousSection,
            dropdownData: {
              ...previousSection.dropdownData,
              locations: updatedGroupedLocations,
              transportationOptions: updatedTransportationOptions,
            } as LocationData,
            isLoading: false,
            value: generateLocationValue({
              locations: updatedGroupedLocations,
              transportationOptions: updatedTransportationOptions,
            }),
          };
        });
      };

      if (
        !dataDestinationHierarchy?.departures ||
        !dataDestinationHierarchy?.destinations
      )
        return;

      updateDestinationsAndDeparturesInLocationSection(
        dataDestinationHierarchy,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDestinationHierarchy]);

    useEffect(() => {
      const defaultValues = mergeObjects(
        travelPortalSearchSettings,
        urlDefaultValues,
      );

      const departureDateParameter = defaultValues.departureDate
        ? parseDanishDate(defaultValues.departureDate)
        : undefined;

      const updateDatesInSections = (data: AdaptSearchAvailableDates) => {
        setDateSection(previousSection => {
          const availableDates =
            data.result.map(item => parseDanishDate(item.departureDate)) || [];
          const availablePeriodTypes = getAvailablePeriodTypesFromDates(data);
          const areaDefaultPeriodType = getTravelPortalAreaDefaults(
            travelPortalSearchSettings,
            area,
          ).defaultPeriodType as string | undefined;

          const currentDuration =
            areaDefaultPeriodType ??
            (previousSection.dropdownData as DateData).durationOptions?.find(
              option => option.isSelected,
            )?.id ??
            getDuration();
          const durationOptionsFromSearchSettings =
            getDurationOptionsFromSearchSettings(currentDuration as string);
          const filteredDurationOptions =
            durationOptionsFromSearchSettings.filter(
              option =>
                !!option.id &&
                availablePeriodTypes.includes(
                  option.id as TravelPortal.Properties.PeriodType,
                ),
            );
          const selectedDurationId =
            filteredDurationOptions.find(option => option.isSelected)?.id ||
            filteredDurationOptions[0]?.id;

          const durationOptions = filteredDurationOptions.map(option => ({
            ...option,
            isSelected: option.id === selectedDurationId,
          }));
          const activeDuration = (selectedDurationId ?? getDuration()) as
            | string
            | undefined;

          // Written by this browser (older cookies as an ISO moment), so native
          // parsing gives back the selected day
          const contextDate = departureDate
            ? new Date(departureDate)
            : undefined;
          const savedDate =
            contextDate ??
            (previousSection.dropdownData as DateData)?.selectedDate;
          const today = new Date();

          today.setHours(0, 0, 0, 0);

          // Check if saved date from cookie is still valid
          const isSavedDateValid =
            savedDate &&
            savedDate >= today &&
            availableDates.some(date => validateSameDate(date, savedDate));

          const isUrlDateAvailable =
            departureDateParameter &&
            availableDates.some(date =>
              validateSameDate(date, departureDateParameter),
            );

          // Use saved date if valid, otherwise use URL param if available, otherwise first available date
          const selectedDate = isSavedDateValid
            ? savedDate
            : isUrlDateAvailable
              ? departureDateParameter
              : availableDates[0];
          const formattedStartDate = selectedDate
            ? dateFormatter(selectedDate).format('D. MMM YYYY')
            : '';

          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as DateData),
              availableDates: activeDuration === 'custom' ? [] : availableDates,
              durationOptions,
              maxDate: undefined,
              minDate: undefined,
              rangeSelect: activeDuration === 'custom',
              rangeSize:
                activeDuration === 'custom'
                  ? undefined
                  : (previousSection.dropdownData as DateData).rangeSize,
              selectedDate,
            },
            isLoading: false,
            isNotified: !isSavedDateValid && !isUrlDateAvailable,
            value: generateDateSectionValueString({
              duration: activeDuration,
              startDate: formattedStartDate,
              t,
            }),
          };
        });
      };

      if (!dataAvailableDates) return;

      updateDatesInSections(dataAvailableDates);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAvailableDates]);

    const invalidatePersonsSection = () => {
      setPersonsSection(previousSection => {
        if (isPersonsSectionValid(previousSection)) {
          return {
            ...previousSection,
            isNotified: false,
          };
        }

        return {
          ...previousSection,
          isNotified: true,
        };
      });
    };

    const handleClickOutside = () => {
      setActiveSection(-1);
    };

    useUiEventOutsideHandler(rootRef, handleClickOutside);

    const updateDepartureInLocationSection = (transportationId: string) => {
      setLocationSection(previousSection => {
        const dropdownData = previousSection.dropdownData as LocationData;

        const updatedTransportationOptions =
          dropdownData.transportationOptions.map(option => ({
            ...option,
            isSelected: option.id === transportationId,
          }));

        const updatedGroupedLocations = dropdownData?.locations?.map(group => ({
          ...group,
          options: group.options.map(destination => {
            const isAvailableForDeparture =
              destination.departureCodes?.includes(transportationId);

            return {
              ...destination,
              checked: isAvailableForDeparture ? destination.checked : false,
              disabled: !isAvailableForDeparture,
            };
          }),
        }));

        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as LocationData),
            locations: updatedGroupedLocations,
            transportationOptions: updatedTransportationOptions,
          } as LocationData,
          value: generateLocationValue({
            locations: updatedGroupedLocations,
            transportationOptions: updatedTransportationOptions,
          }),
        };
      });
    };

    const handleClickSection = useCallback(
      (sectionId: number) => {
        if (activeSection === sectionId) {
          setActiveSection(-1);
        } else {
          setActiveSection(sectionId);

          if (!rootRef.current) return;

          if (!isLaptop) return;

          const LARGE_SCROLL_OFFSET = 144;
          const SMALL_SCROLL_OFFSET = 24;
          const elementPosition = rootRef.current.getBoundingClientRect().top;

          if (
            (document.body.getBoundingClientRect().top -
              rootRef.current.getBoundingClientRect().top) *
              -1 <=
            LARGE_SCROLL_OFFSET
          ) {
            window.scrollTo({
              behavior: 'smooth',
              top: elementPosition + window.scrollY - LARGE_SCROLL_OFFSET,
            });
          } else {
            window.scrollTo({
              behavior: 'smooth',
              top: elementPosition + window.scrollY - SMALL_SCROLL_OFFSET,
            });
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [activeSection, isLaptop],
    );

    const handleClickClose = useCallback(() => {
      setActiveSection(-1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickNext = useCallback(() => {
      setActiveSection(activeSection + 1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const handleClickBack = useCallback(() => {
      setActiveSection(activeSection - 1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const handleChangeDuration = useCallback(
      (durationId: string) => {
        const departureDateParameter = getDepartureDateParameter();

        if (durationId === 'custom') {
          setDateSection(previousSection => {
            const durationOptions = (
              (previousSection?.dropdownData as DateData)?.durationOptions ?? []
            ).map(option => ({
              ...option,
              isSelected: option.id === durationId,
            }));

            return {
              ...previousSection,
              dropdownData: {
                ...(previousSection.dropdownData as DateData),
                availableDates: [],
                durationOptions,
                maxDate: undefined,
                minDate: undefined,
                rangeSelect: true,
                rangeSize: undefined,
                selectedDate: undefined,
                selectedMaxDate: undefined,
              },
              value: generateDateSectionValueString({
                duration: durationId,
                startDate: undefined,
                t,
              }),
            };
          });

          return;
        }

        if (!dataAvailableDates) return;

        const filteredDates =
          dataAvailableDates.result
            ?.filter(item =>
              item.availablePeriodTypes?.includes(
                durationId as TravelPortal.Properties.PeriodType,
              ),
            )
            .map(item => parseDanishDate(item.departureDate)) ?? [];

        const isDateAvailable =
          departureDateParameter &&
          filteredDates.some(date =>
            validateSameDate(date, departureDateParameter),
          );

        let selectedDate = departureDateParameter;
        let noValidDates = false;

        if (!isDateAvailable) {
          selectedDate = filteredDates[0];

          if (!selectedDate) {
            noValidDates = true;
          }
        }

        const formattedDate = dateFormatter(selectedDate).format('D. MMM YYYY');

        setDateSection(previousSection => {
          const durationOptions = (
            (previousSection?.dropdownData as DateData)?.durationOptions ?? []
          ).map(option => ({
            ...option,
            isSelected: option.id === durationId,
          }));

          const selectedOption = durationOptions.find(opt => opt.isSelected);
          const baseDropdownData = {
            ...(previousSection?.dropdownData as DateData),
            durationOptions,
            rangeSelect: false,
            rangeSize: selectedOption?.rangeSize,
          };

          return {
            ...previousSection,
            dropdownData: noValidDates
              ? {
                  ...baseDropdownData,
                  availableDates: [],
                  selectedDate: undefined,
                }
              : {
                  ...baseDropdownData,
                  availableDates: filteredDates,
                  selectedDate,
                },
            isNotified: false,
            value: noValidDates
              ? ''
              : generateDateSectionValueString({
                  duration: durationId,
                  startDate: formattedDate,
                  t,
                }),
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dataAvailableDates],
    );

    const handleChangeTransportation = useCallback(
      (transportationId: string) => {
        updateDepartureInLocationSection(transportationId);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangeLocations = useCallback(
      ({
        destinationRegions,
        destinations,
        locations,
      }: LocationChangeParameters) => {
        setLocationSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              ...(previousSection.dropdownData as LocationData),
              destinationRegionIds: destinationRegions,
              destinations,
              locations,
            },
            isNotified: false,
            value: generateLocationValue({
              locations,
              transportationOptions: (
                previousSection.dropdownData as LocationData
              ).transportationOptions,
            }),
          };
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const handleChangePersons = useCallback((roomsState: RoomsState) => {
      setPersonsSection(previousSection => {
        return {
          ...previousSection,
          dropdownData: {
            ...(previousSection.dropdownData as PersonsData),
            roomsState: roomsState,
          },
          isLoading: false,
          value: generatePersonsSectionValueString({
            roomsState,
            t,
          }),
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeDate = useCallback((newDates: Date[]) => {
      const isRange = newDates.length === 2;
      const startDate = newDates[0];
      const endDate = isRange ? newDates[1] : undefined;

      const formattedStartDate = startDate
        ? dateFormatter(startDate).format('D. MMM YYYY')
        : undefined;
      const formattedEndDate = endDate
        ? dateFormatter(endDate).format('D. MMM YYYY')
        : undefined;

      const valueString = generateDateSectionValueString({
        duration: getDuration(),
        endDate: formattedEndDate,
        startDate: formattedStartDate,
        t,
      });

      setDateSection(previousSection => {
        const previousDropdown = previousSection.dropdownData as DateData;

        return {
          ...previousSection,
          dropdownData: {
            ...previousDropdown,
            availableDates: isRange ? [] : previousDropdown.availableDates,
            rangeSelect: isRange,
            rangeSize: isRange ? undefined : previousDropdown.rangeSize,
            selectedDate: startDate,
            selectedMaxDate: endDate,
          },
          isLoading: false,
          isNotified: false,
          value: valueString,
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateSearchBarStateFromUrl = () => {
      const areaFromDefaults = urlDefaultValues.area;
      const resolvedAreaId = areaFromDefaults
        ? getAreaByValue({
            travelPortalSearchSettings,
            value: areaFromDefaults,
          })
        : undefined;

      const values = {
        adultsNumber: adultsNo || adultsNumber?.toString() || '',
        area: resolvedAreaId || areaFromDefaults || area,
        childrenAges: childrenAges || urlDefaultValues.childrenAges?.join(','),
        departureDate: departureDate || urlDefaultValues.departureDate,
        departureId: departureIds || urlDefaultValues.departureId,
        durationRange: urlDefaultValues.durationRange,
      };

      if (values.area) {
        setArea(values.area);
      }

      const rooms = [
        {
          childrenAges: [],
          numberOfAdults: adultsNo || adultsNumber || 0,
          numberOfChildren: 0,
        },
      ] as Room[];

      if (values.adultsNumber) {
        rooms[0].numberOfAdults = parseInt(values.adultsNumber, 10);
      }

      if (values.childrenAges) {
        rooms[0].childrenAges = values.childrenAges
          .split(',')
          .map((age: string) => parseInt(age, 10));
        rooms[0].numberOfChildren = rooms[0].childrenAges.length;
      }

      if (values.departureId) {
        updateDepartureInLocationSection(values.departureId);
      }

      if (values.departureDate) {
        const departureDate = new Date(values.departureDate);

        if (values.durationRange !== undefined) {
          handleChangeDate([
            departureDate,
            getRangeEndDate(departureDate, parseInt(values.durationRange, 10)),
          ]);
        } else {
          handleChangeDate([departureDate]);
        }
      }

      handleChangePersons({
        automaticRoomAssignment:
          !!travelPortalSearchSettings?.automaticRoomAssignment,
        rooms,
      });
    };

    const handleClickSearch = useCallback(() => {
      searchPageEvents$.next({ type: SearchPageEventType.SEARCH });
      const locale = getLocale();

      const constructAndUpdateSearchUrl = () => {
        const adultsNumber = getAdultCount()?.toString() as string;
        const childrenAges = getChildrenAges();
        const departureDate = getDepartureDate() as string;
        const departureIds = getTransportationMethod() as string;
        const { countryIds, destinationIds } = getCheckedLocationCodes();
        const durationRange = getDurationRange()?.toString() as string;
        const durationType = getDuration() as string;

        const parameters = new URLSearchParams({
          batchSize: travelPortalSearchSettings?.batchSize.toString() ?? '10',
          departureDate,
          departureIds,
          durationType,
          locale: getCulture(locale),
          sortBy:
            travelPortalSearchSettings?.selectedSortBy ?? 'websitePriority',
        });

        if (countryIds) {
          parameters.append('countryIds', countryIds);
        }

        if (destinationIds) {
          parameters.append('destinationIds', destinationIds);
        }

        parameters.append('childrenAges', childrenAges);
        parameters.append('adultsNumber', adultsNumber);
        parameters.append(
          'area',
          getArea({ areaId: area, travelPortalSearchSettings }) ?? '',
        );

        if (getDuration() === 'custom') {
          parameters.append('durationRange', durationRange);
        }

        const parametersObject: Record<string, any> = {};

        parameters.forEach((value, key) => {
          parametersObject[key] = value;
        });

        setTriggeredBy('searchButton');

        updateCookies({
          adultsNumber,
          adultsPerRoom: '',
          childrenAges,
          childrenAgesPerRoom: '',
          // A plain date like the url's, so it reads back as the same Danish
          // date in any timezone (an ISO moment would shift it)
          departureDate: extractDepartureDate(dateSection),
          departureIds,
          periodType: durationType,
        });

        addRecentSearch({
          locationSection: {
            value: recentSearchLocationText,
          },
          parameters,
        });

        updateUrlWithParameters({
          anchorLinkId: configurationFlowAnchorLinkId,
          newParameters: parametersObject,
          overwrite: true,
          redirectUrl: travelPortalSearchSettings?.searchPageUrl,
          router,
          shouldNavigate,
          shouldRefresh,
        });
      };

      invalidatePersonsSection();

      if (
        getDuration() === undefined ||
        getTransportationMethod() === undefined ||
        getDepartureDate() === undefined ||
        !isPersonsSectionValid(personsSection)
      ) {
        return;
      }

      const departureDate = dateFormatter(getDepartureDate()).locale('en');
      const countries = [];
      const destinations = [];

      trackSubmitSearchEvent({
        search_details: {
          adults: Number(getAdultCount()),
          children: Number(
            getChildrenAges()?.split(',')[0] !== ''
              ? getChildrenAges()?.split(',').length
              : 0,
          ),
          children_ages: String(getChildrenAges()?.split(',').join(', ')),
          country: String(countries?.join(', ').toLowerCase()),
          departure_date: String(departureDate.format('YYYY-MM-DD')),
          departure_day: String(departureDate.date()),
          departure_month: String(departureDate.format('MMM').toLowerCase()),
          departure_week: String(departureDate.week()),
          departure_week_day: String(
            departureDate.format('dddd').toLowerCase(),
          ),
          departure_year: String(departureDate.year()),
          destination: String(destinations?.join(', ').toLowerCase()),
          duration: getDuration() === 'week' ? 7 : 3,
          duration_type: String(getDuration()),
          travelform: String(
            dataDestinationHierarchy?.departures
              .find(departure => departure.id === getTransportationMethod())
              ?.headline.toLowerCase(),
          ),
        },
      });

      setActiveSection(-1);
      constructAndUpdateSearchUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      area,
      dateSection,
      locationSection,
      travelPortalSearchSettings,
      shouldNavigate,
      shouldRefresh,
      recentSearchLocationText,
      personsSection,
      dataDestinationHierarchy,
      router,
      configurationFlowAnchorLinkId,
    ]);

    useImperativeHandle(ref, () => ({
      search: handleClickSearch,
    }));

    useEffect(() => {
      if (
        travelPortalSearchSettings &&
        travelPortalSearchSettings?.availableAreas.length > 0
      ) {
        setAreaSection(previousSection => {
          return {
            ...previousSection,
            dropdownData: {
              areas: travelPortalSearchSettings.availableAreas,
            } as AreaData,
          };
        });

        setArea(travelPortalSearchSettings.availableAreas[0].id);
      }

      setupDurationOptions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [travelPortalSearchSettings]);

    useEffect(() => {
      updateSearchBarStateFromUrl();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
      if (!area) return;

      const defaultDepartureCode = getTravelPortalAreaDefaults(
        travelPortalSearchSettings,
        area,
      ).defaultDepartureCode as string;

      updateDepartureInLocationSection(defaultDepartureCode);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area]);

    useEffect(() => {
      const { countryIds, destinationIds } = getCheckedLocationCodes();

      setDepartureIds(getTransportationMethod() as string);
      setDestinationIds(destinationIds);
      setCountryIds(countryIds);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSection]);

    useEffect(() => {
      const childrenAges = getChildrenAges();
      const adultsNumber = getAdultCount()?.toString() as string;

      setChildrenAges(childrenAges);
      setAdultsNo(adultsNumber);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personsSection]);

    useEffect(() => {
      setDepartureDate(getDepartureDate() as string);
      setPeriodType(getDuration() as string);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateSection]);

    const isEnabledSearchButton = useMemo(
      () =>
        getDuration() !== undefined &&
        getTransportationMethod() !== undefined &&
        getDepartureDate() !== undefined,
      [getDuration, getTransportationMethod, getDepartureDate],
    );

    // Trigger default search when entering search page with no URL parameters
    useEffect(() => {
      const hasUrlParams =
        urlDefaultValues?.departureDate ||
        urlDefaultValues?.departureId ||
        urlDefaultValues?.destinationIds ||
        urlDefaultValues?.durationType;

      // Only trigger if autoSearch is enabled and no URL params and search bar is ready
      if (
        autoSearch &&
        !hasUrlParams &&
        getDuration() &&
        isEnabledSearchButton
      ) {
        handleClickSearch();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabledSearchButton, autoSearch]);

    const sections = useMemo(
      () =>
        compact
          ? [areaSection, locationSection, personsSection, dateSection]
          : [locationSection, personsSection, dateSection],
      [compact, areaSection, locationSection, personsSection, dateSection],
    );

    return (
      <div
        className={classNames(styles.component, className, {
          [styles.isOpen]: activeSection !== -1,
        })}
      >
        {/* Pointer-only backdrop, keyboard users close the dropdown with its close button */}
        <button
          aria-hidden="true"
          aria-label={t('items.common.words.close')}
          className={styles.overlay}
          onClick={() => {
            setActiveSection(-1);
          }}
          tabIndex={-1}
          type="button"
        />

        <div ref={rootRef} className={styles.content}>
          <SearchBarWrapper
            activeSection={activeSection}
            areaSection={areaSection}
            buttonLabel={t('items.common.words.search')}
            buttonMode={buttonMode}
            canSubmit={isEnabledSearchButton}
            compact={compact}
            customRangeStyle={getDuration() !== 'custom'}
            isLoadingDate={isFetchingAvailableDates}
            isLoadingLocations={isFetchingDestinationHierarchy}
            onChangeDate={handleChangeDate}
            onChangeDuration={handleChangeDuration}
            onChangeLocations={handleChangeLocations}
            onChangePersons={handleChangePersons}
            onChangeTransportation={handleChangeTransportation}
            onClickBack={handleClickBack}
            onClickClose={handleClickClose}
            onClickNext={handleClickNext}
            onClickSearch={handleClickSearch}
            onClickSection={handleClickSection}
            searchSettings={travelPortalSearchSettings}
            sections={sections}
          />
        </div>
      </div>
    );
  },
);

SearchBar.displayName = 'SearchBar';
